import React from 'react';
import {
  ArrowOutwardRounded,
  MyLocationRounded,
  RouteRounded,
  TwoWheelerRounded
} from '@mui/icons-material';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../common/sharedFunctions';

const accent = '#B7EE00';
const ink = '#111411';

const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = [
    {
      description: t('set_route_description', {
        defaultValue: 'Choose your pickup and destination in just a few taps.'
      }),
      icon: <MyLocationRounded sx={{ fontSize: 27 }} />,
      number: '01',
      title: t('set_your_route', { defaultValue: 'Set your route' })
    },
    {
      description: t('choose_ride_description', {
        defaultValue: 'See available ride options and choose what suits your trip.'
      }),
      icon: <TwoWheelerRounded sx={{ fontSize: 29 }} />,
      number: '02',
      title: t('choose_your_ride', { defaultValue: 'Choose your ride' })
    },
    {
      description: t('ride_confidence_description', {
        defaultValue: 'Meet your verified rider and follow your journey with confidence.'
      }),
      icon: <RouteRounded sx={{ fontSize: 28 }} />,
      number: '03',
      title: t('ride_with_confidence', { defaultValue: 'Ride with confidence' })
    }
  ];

  return (
    <Box
      component="section"
      id="how-it-works"
      sx={{
        backgroundColor: '#F3F5EE',
        color: ink,
        py: { xs: 7, sm: 9, md: 11 },
        scrollMarginTop: '100px'
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          mb: { xs: 4, md: 5 }
        }}>
          <Box>
            <Typography sx={{
              color: '#65705F',
              fontFamily: FONT_FAMILY,
              fontSize: '0.76rem',
              fontWeight: 900,
              letterSpacing: '0.13em',
              mb: 1.25,
              textTransform: 'uppercase'
            }}>
              {t('how_it_works', { defaultValue: 'How it works' })}
            </Typography>
            <Typography component="h2" sx={{
              color: ink,
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '2rem', sm: '2.65rem', md: '3.25rem' },
              fontWeight: 850,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              maxWidth: 670
            }}>
              {t('three_steps_heading', {
                defaultValue: 'Your next ride, in three simple steps.'
              })}
            </Typography>
          </Box>
          <Typography sx={{
            color: '#626A62',
            fontFamily: FONT_FAMILY,
            fontSize: '0.98rem',
            lineHeight: 1.7,
            maxWidth: 380
          }}>
            {t('three_steps_subtitle', {
              defaultValue: 'A quick, clear booking flow designed for everyday travel around Shillong.'
            })}
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gap: { xs: 2, md: 2.5 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }
        }}>
          {steps.map((step) => (
            <Box
              key={step.number}
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E1E5DC',
                borderRadius: '22px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: 240, md: 275 },
                overflow: 'hidden',
                p: { xs: 2.5, sm: 3 },
                position: 'relative',
                transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
                '&:hover': {
                  borderColor: 'rgba(183,238,0,0.8)',
                  boxShadow: '0 20px 45px rgba(17,20,17,0.09)',
                  transform: 'translateY(-6px)'
                }
              }}
            >
              <Box sx={{
                alignItems: 'center',
                backgroundColor: ink,
                borderRadius: '15px',
                color: accent,
                display: 'flex',
                height: 52,
                justifyContent: 'center',
                mb: 4,
                width: 52
              }}>
                {step.icon}
              </Box>
              <Typography sx={{
                color: ink,
                fontFamily: FONT_FAMILY,
                fontSize: '1.25rem',
                fontWeight: 850,
                mb: 1.2
              }}>
                {step.title}
              </Typography>
              <Typography sx={{
                color: '#697169',
                fontFamily: FONT_FAMILY,
                fontSize: '0.92rem',
                lineHeight: 1.65,
                maxWidth: 300
              }}>
                {step.description}
              </Typography>
              <Typography aria-hidden="true" sx={{
                bottom: -20,
                color: '#F0F2EB',
                fontFamily: FONT_FAMILY,
                fontSize: '7rem',
                fontWeight: 900,
                letterSpacing: '-0.08em',
                lineHeight: 1,
                position: 'absolute',
                right: 16,
                userSelect: 'none'
              }}>
                {step.number}
              </Typography>
              <ArrowOutwardRounded sx={{
                bottom: 22,
                color: ink,
                fontSize: 20,
                position: 'absolute',
                right: 22,
                zIndex: 1
              }} />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
